"use client";

import campaignApi from "@/api/campaign";
import EnhancedTable from "@/hocs/EnhancedTable";
import Campaign from "@/interfaces/campaign.interface";

import { formatDateWithTimezone } from "@/utils/func";
import useTimezoneStore from "@/stores/timezone.store";
import Link from "next/link";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { RefObject, use, useRef, useState } from "react";
import { SyncCampaign } from "@/components/Campaign/SyncCampaign";
import CustomButton from "@/components/common/CustomButton";
import { useToastStore } from "@/stores/toast.store";
import { useClickOutside } from "primereact/hooks";
import ResourceGrantDialog from "@/components/ResourceGrantDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";

const ROWS_PER_PAGE = 10;

const CampaignsList = () => {
  const displayTimeZone = useTimezoneStore((s) => s.timezone);
  const [showSyncCampaign, setShowSyncCampaign] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sharingCampaign, setSharingCampaign] = useState<Campaign | null>(null);
  const [currentPageCampaigns, setCurrentPageCampaigns] = useState<Campaign[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const currentUser = useCurrentUser();
  const hasCampaignAll = currentUser?.isSuperAdmin === true || (
    (currentUser?.permissionCodes?.includes("campaign:read:all") ?? false) &&
    (currentUser?.permissionCodes?.includes("campaign:update:all") ?? false)
  );
  const canShare = (campaign: Campaign) =>
    hasCampaignAll || campaign.userId === currentUser?._id;
  const anyCanShare = currentPageCampaigns.some(canShare);
  useClickOutside(ref as RefObject<Element>, () => {
    setShowSyncCampaign(false);
  });
  const handleSyncCampaign = async (productId: string) => {
    if (!productId.trim()) {
      useToastStore
        .getState()
        .showToast("error", "Error", "Product ID cannot be empty");
      return;
    }

    setLoading(true);

    try {
      await campaignApi.syncManual(productId);
      useToastStore
        .getState()
        .showToast(
          "success",
          "Queued",
          "Campaign has been added to the sync queue",
        );

      setShowSyncCampaign(false);
    } catch (error) {
      useToastStore
        .getState()
        .showToast("error", "Error", "Failed to sync campaign");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col rounded-lg bg-white p-6 shadow-lg">
      <EnhancedTable
        size="small"
        cachedKey="campaigns"
        fetchData={async (params) => {
          const result = await campaignApi.getMany({ ...params, view: "list" });
          setCurrentPageCampaigns((result.data ?? []) as Campaign[]);
          return result;
        }}
        itemsPerPage={ROWS_PER_PAGE}
        searchPlaceholder="Search campaigns by id, campaign name , product id"
        title="Campaigns"
        scrollable
        // suffix={
        //   <div className="flex flex-col items-end">
        //     <Button
        //       onClick={() => setShowSyncCampaign(!showSyncCampaign)}
        //       className="w-max"
        //       style={{
        //         fontWeight: "bold",
        //       }}
        //     >
        //       Sync
        //     </Button>
        //     <div
        //       className="relative h-max w-max"
        //       style={{ display: showSyncCampaign ? "flex" : "none" }}
        //     >
        //       <SyncCampaign
        //         ref={ref}
        //         handleSyncCampaign={handleSyncCampaign}
        //         loading={loading}
        //       />
        //     </div>
        //   </div>
        // }
      >
        {/* <Column
          field="store"
          header="Store"
          style={{ minWidth: "50px", whiteSpace: "nowrap", textAlign: "start" }}
          body={(rowData: Campaign) => (
            <Image
              crossOrigin="anonymous"
              src={STORE[rowData.store].icon}
              alt={rowData.store}
              width="40"
              height="40"
              style={{
                borderRadius: "50%",
              }}
              loading="lazy"
            />
          )}
        /> */}

        <Column
          field="name"
          header="Campaign Name"
          rowSpan={1}
          body={(rowData: Campaign) => (
            <p>
              <Link
                href={`/campaigns/${rowData._id}`}
                className="line-clamp-2 w-full transition-all hover:underline"
              >
                {rowData.title}
              </Link>
              {/* <span
                className="text-sm text-gray-500 cursor-pointer hover:text-zinc-700"
                title="Click to copy Order ID"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!rowData._id) return;
                  copyToClipboard(rowData._id);
                }}
              >
                {rowData._id}
              </span> */}
            </p>
          )}
          style={{ minWidth: "400px" }}
        />

        <Column
          header="Product ID"
          field="productId"
          body={(rowData: Campaign) => {
            return (
              <Link
                className="rounded-full py-2 text-sm whitespace-nowrap text-gray-600 hover:underline"
                href={`https://${rowData.store}/products/${rowData.productSlug}`}
                target="_blank"
                title="Click to view product details"
              >
                {rowData.productId}
              </Link>
            );
          }}
          style={{ width: "100px", whiteSpace: "nowrap", textAlign: "start" }}
        />
        <Column
          header="Is Active?"
          body={(rowData: Campaign) => (
            <div>
              {rowData.status.toLowerCase() === "active" ? (
                <i
                  className="pi pi-check-circle text-green-600"
                  style={{ fontSize: "1rem" }}
                />
              ) : (
                <></>
              )}
            </div>
          )}
          style={{ width: "100px", whiteSpace: "nowrap", textAlign: "start" }}
        />
        <Column
          header="Timestamp"
          field="createdAt"
          body={(rowData: Campaign) => {
            return (
              <p>
                <span
                  className="text-gray-500"
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  {formatDateWithTimezone(rowData.createdAt, displayTimeZone)}
                </span>
                <span className="flex h-max w-full items-center justify-center">
                  <i
                    className="pi pi-arrow-down text-sm text-gray-500"
                    style={{
                      fontSize: "0.5rem",
                    }}
                  ></i>
                </span>
                <span
                  className="text-gray-500"
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  {formatDateWithTimezone(rowData.updatedAt, displayTimeZone)}
                </span>
              </p>
            );
          }}
          style={{ width: "70px", whiteSpace: "nowrap", textAlign: "start" }}
        />
        {/* <Column
          header="Actions"
          body={(campaign: Campaign) => {
            return (
              <div className="flex items-center justify-center gap-1">
                <CustomButton
                  tooltip="Re-sync campaign"
                  tooltipOptions={{ position: "top" }}
                  onClick={() => {
                    handleSyncCampaign(campaign.productId);
                  }}
                  className="flex cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
                >
                  <i
                    className="pi pi-sync"
                    style={{
                      fontSize: "1.2rem",
                    }}
                  ></i>
                </CustomButton>
              </div>
            );
          }}
          style={{
            textAlign: "center",
            width: "70px",
            whiteSpace: "nowrap",
            verticalAlign: "middle",
          }}
        /> */}
        {anyCanShare && (
          <Column
            header="Actions"
            body={(campaign: Campaign) => (
              <div className="flex items-center justify-end gap-1">
                {canShare(campaign) && (
                  <span
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSharingCampaign(campaign);
                    }}
                  >
                    <i
                      className="pi pi-share-alt text-purple-600"
                      style={{ fontSize: "1.2rem" }}
                    ></i>
                  </span>
                )}
              </div>
            )}
            style={{ width: "70px", textAlign: "right" }}
            headerStyle={{ textAlign: "right" }}
          />
        )}
      </EnhancedTable>
      <ResourceGrantDialog
        visible={Boolean(sharingCampaign)}
        resourceEndpoint="campaigns"
        resourceType="campaign"
        resourceId={sharingCampaign?._id}
        resourceName={sharingCampaign?.title}
        ownerId={sharingCampaign?.userId}
        relatedResourceLabel="form & template"
        loadRelatedResources={async (campaignId) => {
          const response = await campaignApi.getDetail(campaignId);
          const detail = response.data;
          if (!detail) return [];
          const related = [];
          if (detail.customizationForm) {
            related.push({
              resourceEndpoint: "customization-forms",
              resourceType: "customization_form",
              resourceId: detail.customizationForm._id,
              name: detail.customizationForm.name,
            });
          }
          if (detail.artwork) {
            related.push({
              resourceEndpoint: "artworks",
              resourceType: "artwork",
              resourceId: detail.artwork._id,
              name: detail.artwork.name,
            });
          }
          if (detail.template) {
            related.push({
              resourceEndpoint: "templates",
              resourceType: "template",
              resourceId: detail.template._id,
              name: detail.template.name,
            });
          }
          return related;
        }}
        onHide={() => setSharingCampaign(null)}
      />
    </div>
  );
};
export default CampaignsList;
